import { Dialog, Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentLanguage } from 'src/i18n/hooks/useCurrentLanguage';

import { useConfirm } from './hooks/useConfirm';

const getIcon = (type: 'warning' | 'success' | 'error' | 'info') => {
  switch (type) {
    case 'success':
      return (
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
          <CheckCircleIcon className="h-6 w-6 text-green-400" />
        </div>
      );
    case 'error':
      return (
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
          <ExclamationTriangleIcon
            className="h-6 w-6 text-red-600"
            aria-hidden="true"
          />
        </div>
      );
    case 'warning':
      return (
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
          <ExclamationTriangleIcon
            className="h-6 w-6 text-yellow-600"
            aria-hidden="true"
          />
        </div>
      );
    default:
      return (
        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 sm:mx-0 sm:h-10 sm:w-10">
          <XMarkIcon />
        </div>
      );
  }
};

export const Confirm = () => {
  const {
    isOpen,
    close,
    resolve,
    message,
    title,
    confirmationText,
    cancellationText,
    reject,
    icon,
  } = useConfirm();

  const { isVisible, type = 'error' } = icon ?? {};

  const { t } = useTranslation();

  const { language } = useCurrentLanguage();

  const dir = language === 'en' ? 'ltr' : 'rtl';

  const btnRef = useRef(null);

  const handleClose = () => {
    reject?.();
    close();
  };

  const handleConfirm = () => {
    resolve?.(true);
    close();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        dir={dir}
        as="div"
        className="relative z-[11]"
        initialFocus={btnRef}
        onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[11] overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white ltr:text-left rtl:text-right shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    {isVisible && getIcon(type)}
                    <div className="mt-3 sm:mt-0 sm:ml-4 text-center ltr:sm:text-left rtl:sm:text-right">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900">
                        {title || t('common:alert')}
                      </Dialog.Title>
                      {message && (
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">{message}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={handleConfirm}
                    ref={btnRef}>
                    {confirmationText || t('common:yes')}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={handleClose}>
                    {cancellationText || t('common:cancel')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
